<template>
  <el-dialog
    class="column-edit-dialog"
    :title="title"
    :visible.sync="visible_"
    :width="dialogWidth"
    append-to-body
    @opened="initSortable"
    @closed="destroySortable"
  >
    <div ref="list" class="column-edit-list">
      <div v-for="col in columns" :key="col.prop" class="column-edit-item">
        <span class="drag-handle">⋮⋮</span>
        <el-checkbox v-model="col.visible" class="col-label">{{ col.label }}</el-checkbox>
        <div v-if="showWidth" class="col-width">
          <span class="col-width-label">宽</span>
          <el-input-number
            v-model="col.width"
            :min="0"
            :max="800"
            :step="10"
            size="mini"
            controls-position="right"
            :placeholder="col.autoWidth ? col.autoWidth + '（自动）' : '自动'"
            class="col-width-input"
          />
        </div>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button type="text" class="reset-btn" @click="handleReset">恢复默认</el-button>
      <div class="footer-spacer"></div>
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import Sortable from 'sortablejs';

export default {
  name: 'ColumnEditDialog',
  props: {
    visible: { type: Boolean, default: false },
    columns: { type: Array, default: () => [] },
    /** 弹窗标题（用于区分手机端/电脑端） */
    title: { type: String, default: '编辑表格列（拖拽排序 / 勾选显隐）' },
    /** 默认列定义（用于恢复默认），格式 [{prop, label}] */
    defaultColumns: { type: Array, default: () => [] },
    /** 是否显示列宽编辑 */
    showWidth: { type: Boolean, default: true },
    /** 弹窗宽度 */
    dialogWidth: { type: String, default: '520px' }
  },
  data() {
    return {
      sortableInstance: null
    };
  },
  computed: {
    visible_: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    }
  },
  methods: {
    initSortable() {
      this.destroySortable();
      const el = this.$refs.list;
      if (!el) return;
      this.sortableInstance = Sortable.create(el, {
        handle: '.drag-handle',
        animation: 150,
        onEnd: (evt) => {
          const moved = this.columns.splice(evt.oldIndex, 1)[0];
          this.columns.splice(evt.newIndex, 0, moved);
        }
      });
    },
    destroySortable() {
      if (this.sortableInstance) {
        this.sortableInstance.destroy();
        this.sortableInstance = null;
      }
    },
    handleCancel() {
      this.destroySortable();
      this.visible_ = false;
      this.$emit('cancel');
    },
    handleReset() {
      if (!this.defaultColumns || !this.defaultColumns.length) return;
      const defaultProps = new Set(this.defaultColumns.map(c => c.prop));
      const defaultPropOrder = this.defaultColumns.map(c => c.prop);

      // 建立 defaultColumns 中 prop → 列定义 的映射（含 width/minWidth 用于 autoWidth）
      const defaultDefMap = new Map(this.defaultColumns.map(c => [c.prop, c]));

      // 建立现有列的 prop → 列对象 映射
      const existingMap = new Map(this.columns.map(c => [c.prop, c]));

      // 1. 按 defaultColumns 顺序排列默认列
      const orderedDefaults = [];
      for (const prop of defaultPropOrder) {
        let col = existingMap.get(prop);
        if (col) {
          existingMap.delete(prop);
        } else {
          // defaultColumns 中有但现有列表中没有 → 新建
          const def = defaultDefMap.get(prop) || {};
          col = {
            prop: prop,
            label: def.label || prop,
            visible: true,
            width: undefined,
            autoWidth: def.width || def.minWidth
          };
        }
        col.visible = true;
        col.width = undefined;
        // 如果 defaultColumns 列定义中有宽度，更新 autoWidth
        const def = defaultDefMap.get(prop);
        if (def && (def.width || def.minWidth)) {
          col.autoWidth = def.width || def.minWidth;
        }
        orderedDefaults.push(col);
      }

      // 2. 收集非默认列（保留原有顺序）
      const nonDefaults = this.columns.filter(c => !defaultProps.has(c.prop));
      nonDefaults.forEach(col => {
        col.visible = false;
        col.width = undefined;
      });

      // 3. 合并：默认列在前，非默认列在后
      const result = [...orderedDefaults, ...nonDefaults];
      this.columns.splice(0, this.columns.length, ...result);
      this.$nextTick(() => this.initSortable());
    },
    handleConfirm() {
      const visibleCount = this.columns.filter(c => c.visible).length;
      if (visibleCount === 0) {
        this.$message.warning('至少保留一列');
        return;
      }
      this.destroySortable();
      this.visible_ = false;
      this.$emit('confirm', this.columns);
    }
  },
  beforeDestroy() {
    this.destroySortable();
  }
};
</script>

<style scoped lang="scss">
/* ✅ 表格圆角：全站 CardInfoTable 统一用它，想改大小只动这一个变量 */
$table-radius: 6px;

/* ✅ 弹窗本体圆角：比表格略大一圈（外层 8px → 内层 6px），想改只动这一个变量 */
$dialog-radius: 8px;

/* ✅ 弹窗本体圆角（.el-dialog）
   ⚠️ class 写在 <el-dialog> 上会落到根元素 .el-dialog__wrapper，真正的弹窗盒子
      .el-dialog 是它的子元素 —— 所以必须用 ::v-deep 才能命中
      （同 StationInfoGas 里 .parts-dialog-dark ::v-deep .el-dialog 的写法） */
.column-edit-dialog ::v-deep .el-dialog {
  border-radius: $dialog-radius;
  overflow: hidden;
}

.column-edit-list {
  max-height: 400px;
  overflow-y: auto;
  /* ✅ 滚动容器圆角：overflow + border-radius 会把滚动到边缘的条目也裁成同一圆角 */
  border-radius: $table-radius;
}
.column-edit-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  margin-bottom: 2px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: $table-radius;   /* ✅ 原 4px → 统一跟全站表格圆角口径 */
  transition: background 0.2s;
  gap: 8px;
}
.column-edit-item:hover {
  background: #f0f2f5;
}
.drag-handle {
  cursor: grab;
  color: #c0c4cc;
  font-size: 18px;
  margin-right: 2px;
  user-select: none;
  letter-spacing: -2px;
  flex-shrink: 0;
}
.drag-handle:active {
  cursor: grabbing;
}
.col-label {
  flex: 1;
  min-width: 0;
}
.col-width {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.col-width-label {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}
.col-width-input {
  width: 100px;
}
.column-edit-item.sortable-ghost {
  opacity: 0.4;
  background: #e6f7ff;
  border: 1px dashed #409eff;
}
.column-edit-item.sortable-chosen {
  background: #ecf5ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.dialog-footer {
  display: flex;
  align-items: center;
}
.reset-btn {
  color: #909399;
}
.reset-btn:hover {
  color: #409eff;
}
.footer-spacer {
  flex: 1;
}
</style>
